import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorIcon from "@mui/icons-material/HighlightOff";
import {
  Button,
  Grid2 as Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { Tabs, Tab, Box } from "@mui/material";
import {
  LoginIntegrationForm,
  ValidationIntegrationForm,
  IntegrationDataDisplay,
  MappedPlanSlug,
  AgreementMappingFlags,
  normalizeIntegrationData,
  getBlockingIssues,
} from "@modules/integration/components";
import { AgreementsAutocomplete } from "@shared/components";
import { useMutation } from "@tanstack/react-query";
import { getAgreementsByIdService } from "@modules/agreements/services/queries/listAgreements/service";
import { Agreement } from "@modules/agreements/types";
import { useNavigate } from "react-router";
import {
  useLoginIntegrationForm,
  useValidateIntegrationForm,
} from "./controller";
import { getAllMapIntegrationPlansService } from "@modules/mapIntegrationPlans/services/queries/listMapIntegrationPlans/service";
import { getAllPlansService } from "@modules/plans/services/queries/listPlans/service";
import { MapIntegrationPlan } from "@modules/mapIntegrationPlans/types";
import { Plan } from "@modules/plans/types";
import { useAgreementErpPlansQuery } from "@modules/integration/services/queries/getAgreementErpPlans";

type PlanMapItem = {
  integration_plan: MapIntegrationPlan;
  plan?: Plan;
};

/** Rótulo acima do valor — hierarquia por cor/caixa, não por negrito */
function InfoField({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) {
  if (!value) return null;

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontSize: "0.68rem",
          color: "text.secondary",
          display: "block",
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
        {value}
      </Typography>
    </Box>
  );
}

/** Normaliza integration_error, que vem como string ou lista */
function toErrorList(value: unknown): string[] {
  if (typeof value === "string") return value.trim() ? [value.trim()] : [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  return [];
}

/**
 * Cartão único de resultado: diz se funcionou, e quando falha separa o erro da
 * Partiu do erro da integração — são coisas diferentes e antes vinham juntos.
 */
function IntegrationResultHeader({
  responseResult,
  hasContractIssues,
  hasMappings,
}: {
  responseResult: any;
  hasContractIssues: boolean;
  hasMappings: boolean;
}) {
  const theme = useTheme();
  const ok = Boolean(responseResult?.token || responseResult?.result);

  const partiuError: string | undefined = responseResult?.error;
  const integrationErrors = toErrorList(responseResult?.integration_error);
  const listErrors = Array.isArray(responseResult) ? responseResult : [];

  const color = ok ? theme.palette.success.main : theme.palette.error.main;

  return (
    <Paper variant="outlined" sx={{ borderColor: color, overflow: "hidden" }}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ px: 2, py: 1.5, backgroundColor: `${color}14` }}
      >
        {ok ? (
          <CheckCircleIcon sx={{ color, fontSize: 28 }} />
        ) : (
          <ErrorIcon sx={{ color, fontSize: 28 }} />
        )}
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {ok
              ? "Integração respondeu com sucesso"
              : "A integração retornou erro"}
          </Typography>
          {!ok && !hasContractIssues && hasMappings && (
            <Typography variant="body2" color="text.secondary">
              O convênio já tem planos cadastrados e o contrato do cliente está
              válido — entre em contato com a Partiu.
            </Typography>
          )}
        </Box>
      </Stack>

      {(partiuError ||
        integrationErrors.length > 0 ||
        listErrors.length > 0 ||
        responseResult?.message ||
        responseResult?.token) && (
        <Stack spacing={1.5} sx={{ p: 2 }}>
          {responseResult?.token && (
            <InfoField label="Token de acesso" value={responseResult.token} />
          )}

          {partiuError && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontSize: "0.68rem",
                  color: theme.palette.error.main,
                  display: "block",
                }}
              >
                Erro da Partiu
              </Typography>
              <Typography variant="body2">{partiuError}</Typography>
            </Box>
          )}

          {integrationErrors.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontSize: "0.68rem",
                  color: theme.palette.error.main,
                  display: "block",
                }}
              >
                Erro da integração do convênio
              </Typography>
              {integrationErrors.map((error, index) => (
                <Typography key={index} variant="body2">
                  {error}
                </Typography>
              ))}
            </Box>
          )}

          {listErrors.length > 0 && (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {listErrors.map((item: any, index: number) => (
                <li key={index}>
                  <Typography variant="body2">{String(item)}</Typography>
                </li>
              ))}
            </Box>
          )}

          {responseResult?.message && (
            <InfoField label="Mensagem" value={responseResult.message} />
          )}
        </Stack>
      )}
    </Paper>
  );
}

export function Integration() {
  const [infoTab, setInfoTab] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();

  const [agreement, setAgreement] = useState<Agreement>();
  const [responseResult, setResponseResult] = useState<any>();
  const [plansMap, setPlansMap] = useState<PlanMapItem[]>([]);

  const handleSuccessLogin = (data: any) => {
    setResponseResult(data);
  };

  const loginMethods = useLoginIntegrationForm(handleSuccessLogin);
  const validateMethods = useValidateIntegrationForm(handleSuccessLogin);

  const aggrementMethods = useForm({
    defaultValues: {
      agreement: null,
    },
  });

  const agreementId = aggrementMethods.watch("agreement");

  const mappedPlanSlugs: MappedPlanSlug[] = useMemo(
    () =>
      plansMap.map((item) => ({
        plan_slug_integration: item.integration_plan.plan_slug_integration,
        id_plan: item.integration_plan.id_plan,
        product_name: item.integration_plan.product_name,
        plan_display_name: item.plan?.display_name ?? null,
        plan_type: item.integration_plan.plan_type,
      })),
    [plansMap]
  );

  // Catálogo oficial de planos do ERP — fonte da verdade do que precisa mapear
  const { data: erpData } = useAgreementErpPlansQuery(
    agreement?.integration_id && agreementId ? String(agreementId) : null
  );

  const erpSlugs = useMemo(
    () => erpData?.data?.map((plan) => plan.slug).filter(Boolean),
    [erpData]
  );

  /** Flags que definem a chave de match — vêm do map_type do get_plans_erp */
  const mappingFlags: AgreementMappingFlags = useMemo(
    () => ({
      mappedById: erpData?.map_type?.mapped_by_id,
      mappedByProduct: erpData?.map_type?.mapped_by_product,
    }),
    [erpData]
  );

  /** Problemas de contrato (vencido/suspenso/cancelado) explicam a falha sozinhos */
  const contractIssues = useMemo(
    () =>
      getBlockingIssues(
        normalizeIntegrationData(responseResult?.integration_data, mappingFlags)
      ),
    [responseResult, mappingFlags]
  );

  const fetchPlansMap = useMutation({
    mutationFn: async (id: string) => {
      if (!id) return [];

      // A API é paginada: buscar página 1 com page_size grande trunca a lista
      // e faz mapeamento existente aparecer como "sem mapeamento".
      const [plansMaps, plans] = await Promise.all([
        getAllMapIntegrationPlansService(id),
        getAllPlansService(id),
      ]);

      // Lista TODOS os mapeamentos — a chave de match é plan_slug_integration
      const items: PlanMapItem[] = plansMaps
        .map((planMap) => ({
          integration_plan: planMap,
          plan: plans.find((plan) => plan.id === planMap.plan),
        }))
        .sort((a, b) =>
          a.integration_plan.plan_slug_integration.localeCompare(
            b.integration_plan.plan_slug_integration,
            "pt-BR",
            { sensitivity: "base" }
          )
        );

      return items;
    },
    onSuccess: (data) => {
      setPlansMap(data);
    },
  });

  const fetchAgreements = useMutation({
    mutationFn: async (id: string) => {
      if (!id) return null;
      const agreement = await getAgreementsByIdService(id);

      if (agreement?.integrated_login) {
        setInfoTab(0);
      }
      return agreement;
    },
    onSuccess: (data: any) => {
      setAgreement(data);
    },
  });

  useEffect(() => {
    if (agreementId) {
      setAgreement(undefined);
      setResponseResult(undefined);
      setPlansMap([]);
      fetchAgreements.mutate(agreementId);
      fetchPlansMap.mutate(agreementId);
    } else {
      setAgreement(undefined);
      setResponseResult(undefined);
      setPlansMap([]);
    }
  }, [agreementId]);

  const renderInfoContent = () => (
    <Stack spacing={2}>
      <Paper
        elevation={2}
        sx={{
          p: 2,
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.grey[900]
              : theme.palette.grey[50],
          overflowX: "auto",
          wordBreak: "break-all",
        }}
      >
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <InfoField label="Plataforma" value={agreement?.integration_id} />
          <InfoField label="Host" value={agreement?.integration_host} />
          <InfoField
            label="Plataforma de token"
            value={agreement?.integration_token}
          />
          <InfoField label="Login" value={agreement?.integration_login} />
          <InfoField label="Senha" value={agreement?.integration_password} />
        </Stack>

        {agreement?.integrated_login && (
          <Typography variant="body2" color="info.main" sx={{ mt: 1.5 }}>
            Esse login é integrado — a requisição e o resultado (login e senha)
            são direto na integração. Em caso de dúvida, fale com o suporte da
            integração.
          </Typography>
        )}
        <Stack direction="row" justifyContent={"flex-end"} spacing={1} mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`/agreement/edit/${agreement?.id}`)}
          >
            Editar
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );

  return (
    <Grid container width={1} spacing={2}>
      <Paper style={{ padding: "16px", width: "100%" }}>
        <Stack direction="column" spacing={2} mb={2}>
          <Typography variant="h6" gutterBottom>
            Escolha um convênio para testar a integração
          </Typography>
          <FormProvider {...aggrementMethods}>
            <AgreementsAutocomplete
              name="agreement"
              label={"Escolha um convênio"}
              required
              getOptionLabel={(option: any) => {
                const name = option?.name ?? "";
                const integration =
                  option?.integration_id &&
                  String(option.integration_id).trim() !== ""
                    ? option.integration_id
                    : "(Sem integração)";
                return `${name} - ${integration}`;
              }}
            />
          </FormProvider>

          {fetchAgreements.isPending && (
            <Stack alignItems="center" gap={1}>
              <Typography variant="body2">Carregando convênio...</Typography>
            </Stack>
          )}

          {agreement && !agreement?.integration_id && (
            <Typography variant="body2" color="error">
              Este convênio não possui integração configurada.
            </Typography>
          )}

          {agreement && agreement?.integration_id && (
            <Box>{renderInfoContent()}</Box>
          )}
          {agreement && agreement?.integration_id && (
            <Tabs
              value={infoTab}
              onChange={(_, v) => {
                setInfoTab(v);
                setResponseResult(undefined);
              }}
            >
              <Tab label="Login" />
              <Tab label="Validação" />
            </Tabs>
          )}

          {agreement && agreement?.integration_id && (
            <Box mt={2}>
              {infoTab === 0 && (
                <FormProvider {...loginMethods.methods}>
                  <LoginIntegrationForm
                    onSubmit={loginMethods.handleSubmit((data: any) => {
                      data.agreement_id = agreement?.id;
                      data.idp = agreement?.integration_id;
                      if (agreement?.integrated_login) {
                        data.integrated_login = agreement.integrated_login;
                      }
                      return loginMethods.mutateAsync(data);
                    })}
                    isSubmitting={loginMethods.isPending}
                  />
                </FormProvider>
              )}
              {infoTab === 1 && (
                <FormProvider {...validateMethods.methods}>
                  <ValidationIntegrationForm
                    onSubmit={validateMethods.handleSubmit((data: any) => {
                      data.agreement_id = agreement?.id;
                      data.cpf = data.cpf.replace(/\D/g, "");

                      return validateMethods.mutateAsync(data);
                    })}
                    isSubmitting={validateMethods.isPending}
                  />
                </FormProvider>
              )}
            </Box>
          )}

          {responseResult && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <IntegrationResultHeader
                responseResult={responseResult}
                hasContractIssues={contractIssues.length > 0}
                hasMappings={plansMap.length > 0}
              />

              {(responseResult.result || responseResult.integration_data) && (
                <IntegrationDataDisplay
                  data={responseResult}
                  mappedPlanSlugs={mappedPlanSlugs}
                  erpSlugs={erpSlugs}
                  mappingFlags={mappingFlags}
                  onGoToMapping={() => navigate("/integration/plan-mapping")}
                />
              )}
            </Stack>
          )}
        </Stack>
      </Paper>
    </Grid>
  );
}
